import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  Download,
  Users,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Trash2,
  Eye,
  History,
  Plus,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ImportPage() {
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lists, setLists] = useState([]);
  const [history, setHistory] = useState([]);
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [showResult, setShowResult] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  
  const fileInputRef = useRef(null);
  const broadcastFileRef = useRef(null);
  
  // Form state
  const [importType, setImportType] = useState('leads'); // leads or broadcast
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [listName, setListName] = useState('');
  const [source, setSource] = useState('import');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const [listsRes, historyRes, productsRes, tagsRes] = await Promise.all([
        fetch(`${API_URL}/api/import/lists`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/import/history`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/products`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/tags`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (listsRes.ok) setLists(await listsRes.json());
      if (historyRes.ok) setHistory(await historyRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (tagsRes.ok) setTags(await tagsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImportLeads = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    if (selectedProduct) formData.append('product', selectedProduct);
    if (selectedTag) formData.append('tag', selectedTag);
    formData.append('source', source);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/import/contacts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        setShowResult(result);
        toast.success(`${result.imported} contatos importados!`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro na importação');
      }
    } catch (error) {
      toast.error('Erro na importação');
    }
    
    setImporting(false);
    e.target.value = '';
  };

  const handleImportBroadcastList = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!listName.trim()) {
      toast.error('Digite um nome para a lista');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('list_name', listName);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/import/broadcast-list`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Lista "${result.name}" criada com ${result.total_phones} números!`);
        setListName('');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro na importação');
      }
    } catch (error) {
      toast.error('Erro na importação');
    }
    
    setImporting(false);
    e.target.value = '';
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Excluir esta lista?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/import/lists/${listId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Lista excluída');
        fetchData();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewList = async (listId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/import/lists/${listId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedList(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar lista');
    }
  };

  const downloadTemplate = () => {
    const csv = `nome,telefone,email,observacao
João Silva,5511999999999,joao@email.com,Cliente VIP
Maria Santos,5521988888888,maria@email.com,Interessado em Produto 1
Pedro Oliveira,5531977777777,,Retornar contato`;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_importacao.csv';
    link.click();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Importar Base de Dados
            </h1>
            <p className="text-slate-500 mt-1">Importe contatos via CSV ou XLSX</p>
          </div>
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Baixar Template
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Import Leads */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5 text-emerald-600" />
                Importar como Leads
              </CardTitle>
              <CardDescription>
                Cria leads no CRM para acompanhamento e atendimento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Produto</Label>
                  <Select value={selectedProduct || 'none'} onValueChange={(v) => setSelectedProduct(v === 'none' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Etiqueta</Label>
                  <Select value={selectedTag || 'none'} onValueChange={(v) => setSelectedTag(v === 'none' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {tags.map(t => (
                        <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs">Origem</Label>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="import">Importação</SelectItem>
                    <SelectItem value="facebook">Facebook Ads</SelectItem>
                    <SelectItem value="google">Google Ads</SelectItem>
                    <SelectItem value="indicacao">Indicação</SelectItem>
                    <SelectItem value="site">Site</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleImportLeads}
                className="hidden"
              />
              
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Selecionar Arquivo CSV/XLSX
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                Colunas aceitas: nome, telefone, email, observacao
              </p>
            </CardContent>
          </Card>

          {/* Import Broadcast List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send className="w-5 h-5 text-blue-600" />
                Importar Lista de Disparo
              </CardTitle>
              <CardDescription>
                Lista de números para disparo direto (sem criar leads)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Nome da Lista *</Label>
                <Input 
                  placeholder="Ex: Campanha Black Friday"
                  value={listName}
                  onChange={(e) => setListName(e.target.value)}
                />
              </div>
              
              <input
                ref={broadcastFileRef}
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                onChange={handleImportBroadcastList}
                className="hidden"
              />
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => broadcastFileRef.current?.click()}
                disabled={importing || !listName.trim()}
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Selecionar Arquivo
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                Aceita CSV com coluna telefone ou TXT com um número por linha
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Lists & History */}
        <Tabs defaultValue="lists" className="space-y-4">
          <TabsList>
            <TabsTrigger value="lists" className="gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Listas de Disparo ({lists.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              Histórico ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lists">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {lists.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma lista importada</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {lists.map(list => (
                      <div key={list.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900">{list.name}</h3>
                            <p className="text-sm text-slate-500">
                              {list.total?.toLocaleString()} números • {new Date(list.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleViewList(list.id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteList(list.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {history.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma importação realizada</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {history.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4">
                        <div>
                          <h3 className="font-medium text-slate-900">{item.filename}</h3>
                          <p className="text-sm text-slate-500">
                            {new Date(item.imported_at).toLocaleDateString('pt-BR', { 
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })}
                            {item.product && ` • ${item.product}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-emerald-600">
                            <CheckCircle2 className="w-4 h-4 inline mr-1" />
                            {item.imported}
                          </span>
                          {item.duplicates > 0 && (
                            <span className="text-amber-600">
                              <AlertCircle className="w-4 h-4 inline mr-1" />
                              {item.duplicates} dup.
                            </span>
                          )}
                          {item.errors > 0 && (
                            <span className="text-red-600">
                              <XCircle className="w-4 h-4 inline mr-1" />
                              {item.errors} erros
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Result Dialog */}
        <Dialog open={!!showResult} onOpenChange={() => setShowResult(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resultado da Importação</DialogTitle>
            </DialogHeader>
            
            {showResult && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{showResult.total_rows}</p>
                    <p className="text-sm text-slate-500">Total de linhas</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{showResult.imported}</p>
                    <p className="text-sm text-slate-500">Importados</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{showResult.duplicates}</p>
                    <p className="text-sm text-slate-500">Duplicados</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{showResult.errors}</p>
                    <p className="text-sm text-slate-500">Erros</p>
                  </div>
                </div>
                
                {showResult.error_details?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Detalhes dos erros:</p>
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                      {showResult.error_details.map((err, i) => (
                        <p key={i}>{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={() => setShowResult(null)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View List Dialog */}
        <Dialog open={!!selectedList} onOpenChange={() => setSelectedList(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedList?.name}</DialogTitle>
            </DialogHeader>
            
            {selectedList && (
              <div className="space-y-4 py-4">
                <p className="text-sm text-slate-500">
                  {selectedList.total} números • Criada em {new Date(selectedList.created_at).toLocaleDateString('pt-BR')}
                </p>
                
                <div className="max-h-64 overflow-y-auto bg-slate-50 rounded-lg p-3">
                  <div className="grid grid-cols-3 gap-2 text-sm font-mono">
                    {selectedList.phones?.slice(0, 99).map((phone, i) => (
                      <span key={i} className="text-slate-600">{phone}</span>
                    ))}
                  </div>
                  {selectedList.phones?.length > 99 && (
                    <p className="text-center text-slate-500 mt-2">
                      ... e mais {selectedList.phones.length - 99} números
                    </p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
